/**
 * Error Handlers
 * TASK-294: Handle errors and provide recovery actions
 *
 * Provides graceful error handling for meeting prep search
 */

import type { ErrorRecoveryAction, PartialResultsInfo, MeetingPrepSourceType } from './types';
import type { SearchResponse } from '../../types/search.types';
import { isEmailOAuthError, isTimeoutError, isNetworkError, isAuthError } from './validators';

/**
 * Determine recovery action based on error
 */
export function handleError(error: Error | string): ErrorRecoveryAction {
  const errorStr = typeof error === 'string' ? error : error.message;
  const errorName = typeof error === 'string' ? '' : error.name;

  // Abort errors should be ignored
  if (errorName === 'AbortError') {
    return { action: 'ignore' };
  }

  // Auth errors require re-authentication
  if (isAuthError(errorStr)) {
    return {
      action: 'reauth',
      message: 'Session expired. Please log in again.',
    };
  }

  // Timeout errors can be retried
  if (isTimeoutError(errorStr)) {
    return {
      action: 'retry',
      delay: 2000,
      message: 'Request timed out. Retrying...',
    };
  }

  // Network errors can be retried with delay
  if (isNetworkError(errorStr)) {
    return {
      action: 'retry',
      delay: 3000,
      message: 'Network error. Please check your connection.',
    };
  }

  // Default: show error to user
  return {
    action: 'show_error',
    message: errorStr || 'An unexpected error occurred.',
  };
}

/**
 * Handle source-specific errors
 */
export function handleSourceError(
  sourceType: MeetingPrepSourceType,
  error: string
): ErrorRecoveryAction {
  // Email OAuth errors
  if (sourceType === 'email' && isEmailOAuthError(error)) {
    return {
      action: 'show_error',
      message: 'Email not connected. Go to Settings to connect your email.',
    };
  }

  // Use general error handler
  return handleError(error);
}

/**
 * Get partial results info from search response
 */
export function getPartialResultsInfo(response: SearchResponse): PartialResultsInfo {
  const failedSources = response.failedSources;
  const allSourceTypes: MeetingPrepSourceType[] = ['client', 'email', 'market', 'news', 'documents'];

  // Determine successful sources (not in failed list)
  const successfulSources = allSourceTypes.filter(
    (type) => !failedSources.some((f) => f.toLowerCase().includes(type))
  );

  return {
    hasPartialResults: response.status === 'partial' || failedSources.length > 0,
    failedSources,
    warnings: response.warnings,
    successfulSources,
  };
}

/**
 * Generate user-friendly error message
 */
export function getUserFriendlyMessage(error: Error | string): string {
  const errorStr = typeof error === 'string' ? error : error.message;

  if (isAuthError(errorStr)) {
    return 'Your session has expired. Please log in again to continue.';
  }

  if (isTimeoutError(errorStr)) {
    return 'The request took too long. Please try again.';
  }

  if (isNetworkError(errorStr)) {
    return 'Unable to connect to the server. Please check your internet connection.';
  }

  if (isEmailOAuthError(errorStr)) {
    return 'Email access not authorized. Please connect your email in Settings.';
  }

  if (errorStr.includes('404')) {
    return 'The requested resource was not found.';
  }

  if (errorStr.includes('500')) {
    return 'A server error occurred. Please try again later.';
  }

  // Generic fallback
  return 'Something went wrong. Please try again.';
}

/**
 * Get retry delay based on attempt number (exponential backoff)
 */
export function getRetryDelay(attempt: number, baseDelay: number = 1000): number {
  const maxDelay = 30000; // 30 seconds max
  const delay = Math.min(baseDelay * Math.pow(2, attempt), maxDelay);
  // Add jitter (±20%)
  const jitter = delay * 0.2 * (Math.random() - 0.5);
  return Math.round(delay + jitter);
}

/**
 * Check if error should trigger retry
 */
export function shouldRetry(error: Error | string, attempt: number, maxAttempts: number = 3): boolean {
  if (attempt >= maxAttempts) {
    return false;
  }

  const recovery = handleError(error);
  return recovery.action === 'retry';
}

/**
 * Log error for debugging
 */
export function logError(context: string, error: Error | string, metadata?: Record<string, any>): void {
  const errorStr = typeof error === 'string' ? error : error.message;
  const stack = typeof error === 'string' ? undefined : error.stack;

  console.error(`[MeetingPrep:${context}]`, {
    message: errorStr,
    stack,
    ...metadata,
    timestamp: new Date().toISOString(),
  });
}

/**
 * Format error for display in UI
 */
export function formatErrorForDisplay(error: Error | string): {
  title: string;
  description: string;
  canRetry: boolean;
} {
  const recovery = handleError(error);
  const userMessage = getUserFriendlyMessage(error);

  return {
    title: recovery.action === 'reauth' ? 'Authentication Required' : 'Error',
    description: userMessage,
    canRetry: recovery.action === 'retry',
  };
}
